import authService from '@/api/authService';
import paymentApi from '@/api/paymentApi';
import userApi from '@/api/userApi';
import { UserMultiSelect } from '@/components/form/userMultiSelect';
import NavigationBar from '@/components/navigationBar';
import { PageTitle } from '@/components/pageTitle';
import PaymentSettings from '@/components/paymentSettings';
import PaymentSetup from '@/components/paymentSetup';
import { Section } from '@/components/section';
import LanguageSelect from '@/components/settings/languageSelect';
import { MaintenanceSettings } from '@/components/settings/maintenanceSettings';
import { TriggerTestEmail } from '@/components/settings/triggerTestEmail';
import { TriggerUserSync } from '@/components/settings/triggerUserSync';
import { Updater } from '@/components/updater';
import useLocalizeError from '@/hooks/useLocalizeError';
import { handleError, useHandleError } from '@/lib/errors';
import SettingsForm from '@/lib/settingsForm';
import { Breadcrumbs, Checkbox, Container, Skeleton } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { hasClaim, hasRole } from 'common/helpers/claimsHelpers';
import isTruthy from 'common/misc/isTruthy';
import { nanoid } from 'nanoid';
import { useTranslator } from 'translations';

export const Route = createFileRoute('/_auth/settings')({
    component: RouteComponent,
});

function RouteComponent() {
    const t = useTranslator();
    const localizeError = useLocalizeError();
    const [appLogin] = authService.appLogin.useCache();

    const [paymentState, error] = paymentApi.state.useCache();
    useHandleError(error);
    const showPaymentSettings = paymentState?.needsIban ?? false;

    const adminSettings = [
        hasClaim(appLogin?.user, 'sync') && (
            <Section key="system" title={t('settings.system.title')} level={3}>
                <TriggerUserSync />
            </Section>
        ),

        hasClaim(appLogin?.user, 'payment.setup') && (
            <Section key="payment" title={t('settings.payment.title')} level={3}>
                <PaymentSetup />
            </Section>
        ),

        hasRole(appLogin?.user, 'admin') && (
            <Section key="maintenance" title={t('settings.maintenance.title')} level={3}>
                <MaintenanceSettings />
            </Section>
        ),
    ].filter(isTruthy);

    return (
        <PageTitle title={t('loggedInInfo.settings')}>
            <NavigationBar
                breadcrumbs={
                    <Breadcrumbs>
                        <Link to="/">
                            <IconHome />
                        </Link>
                        <Link to="/settings">{t('settings.settings')}</Link>
                    </Breadcrumbs>
                }
            />

            <Container
                size="md"
                css={{
                    width: '100%',
                    padding: '0 var(--mantine-spacing-sm)',
                }}
            >
                <SettingsForm.Form
                    original={appLogin?.user}
                    autoSave={{
                        async save(draft) {
                            try {
                                await userApi.updateSettings(draft.settings ?? {});
                            } catch (e) {
                                handleError(e);
                            }
                        },
                        debounce: { seconds: 2 },
                    }}
                    localizeError={localizeError}
                >
                    {!appLogin?.user ? (
                        <>
                            <Skeleton
                                height={100}
                                css={{ marginTop: 'var(--mantine-spacing-sm)' }}
                            />
                            <Skeleton
                                height={100}
                                css={{ marginTop: 'var(--mantine-spacing-sm)' }}
                            />
                            <Skeleton
                                height={100}
                                css={{ marginTop: 'var(--mantine-spacing-sm)' }}
                            />
                        </>
                    ) : (
                        <>
                            <Section title={t('settings.general')}>
                                <SettingsForm.Field name="settings.language">
                                    {(props) => (
                                        <LanguageSelect
                                            {...props}
                                            label={t('settings.language')}
                                            css={{ width: 200 }}
                                        />
                                    )}
                                </SettingsForm.Field>

                                <SettingsForm.Field name="settings.autoArchive">
                                    {({ value, onChange }) => (
                                        <Checkbox
                                            checked={value ?? false}
                                            onChange={(e) => onChange(e.currentTarget.checked)}
                                            label={t(`settings.autoArchive`)}
                                        />
                                    )}
                                </SettingsForm.Field>
                            </Section>

                            {showPaymentSettings && (
                                <Section title={t('settings.payment.title')}>
                                    <PaymentSettings />
                                </Section>
                            )}

                            <Section title={t('settings.notifications')}>
                                {(['notifyOwnEmail', 'notifyRoleEmail', 'notifySelf'] as const).map(
                                    (prop) => (
                                        <SettingsForm.Field key={prop} name={`settings.${prop}`}>
                                            {({ value, onChange }) => (
                                                <Checkbox
                                                    checked={value ?? false}
                                                    onChange={(e) =>
                                                        onChange(e.currentTarget.checked)
                                                    }
                                                    label={t(`settings.${prop}`)}
                                                />
                                            )}
                                        </SettingsForm.Field>
                                    ),
                                )}

                                {hasRole(appLogin?.user, 'admin') && <TriggerTestEmail />}
                            </Section>

                            <Section title={t('settings.permissions')}>
                                <Updater
                                    value={
                                        appLogin?.user.substitutes?.map(
                                            (substitute) => substitute.userId,
                                        ) ?? []
                                    }
                                    commit={async (value) => {
                                        const add = value.filter(
                                            (id) =>
                                                !appLogin?.user.substitutes?.some(
                                                    (substitute) => substitute.userId === id,
                                                ),
                                        );
                                        const remove =
                                            appLogin?.user.substitutes?.filter(
                                                (substitute) => !value.includes(substitute.userId),
                                            ) ?? [];

                                        for (const userId of add) {
                                            await userApi.updateSubstitute(appLogin.user.id, {
                                                id: nanoid(),
                                                userId,
                                            });
                                        }

                                        for (const substitute of remove) {
                                            await userApi.deleteSubstitute(
                                                appLogin.user.id,
                                                substitute.id,
                                            );
                                        }
                                    }}
                                >
                                    {(value, onChange) => (
                                        <UserMultiSelect
                                            label={t('settings.substitute')}
                                            value={value}
                                            onChange={onChange}
                                        />
                                    )}
                                </Updater>

                                {hasClaim(appLogin.user, 'editAdditionalApprovers', {
                                    forUser: appLogin.user.id,
                                }) && (
                                    <Updater
                                        value={appLogin?.user.additionalApproverIds}
                                        commit={(value) =>
                                            userApi.updateAdditionalAppovers(
                                                appLogin.user.id,
                                                value ?? null,
                                            )
                                        }
                                    >
                                        {(value, onChange) => (
                                            <UserMultiSelect
                                                label={t('settings.additionalApprovers')}
                                                value={value ?? []}
                                                onChange={onChange}
                                            />
                                        )}
                                    </Updater>
                                )}
                            </Section>

                            {adminSettings.length > 0 && (
                                <Section
                                    title={t('settings.admin')}
                                    css={{
                                        border: '1px solid var(--mantine-color-red-5)',
                                        borderRadius: 'var(--mantine-radius-md)',
                                        padding: 'var(--mantine-spacing-md)',
                                    }}
                                >
                                    {adminSettings}
                                </Section>
                            )}
                        </>
                    )}
                </SettingsForm.Form>
            </Container>
        </PageTitle>
    );
}
